import { z } from 'zod'
import { isGoogleMapsEmbedUrl } from '../../google-location/utils/embedUrl'
import {
  MAX_GALLERY_IMAGES,
  MAX_ROOMS_PER_BLOCK,
  MAX_TABLES_PER_ROOM
} from '../config'
import { localized, localizedTextSchema } from './localized'
import { richTextDocSchema } from './richText'

/**
 * Ids only have to be unique inside one document, so they are short strings
 * generated by the editor rather than UUIDs. Same reasoning as the loyalty
 * reward ids: `crypto.randomUUID` is unavailable over plain http, which is how
 * this app is served in development.
 */
export const idSchema = z.string().min(1).max(64)

/**
 * A reference to a picture.
 *
 * Wider than `idSchema` because until there is somewhere to upload to, this
 * holds the URL of a photo that already lives somewhere else, and URLs are long.
 * Widening is backwards compatible: every id that parsed before still parses.
 */
export const assetRefSchema = z.string().min(1).max(2048)

export const alignSchema = z.enum(['left', 'center', 'right'])
export const spacingTokenSchema = z.enum(['S', 'M', 'L', 'XL'])

/*
  Elements that take over a whole row.

  The composition rule from §4 of the brief hangs off this list: these may only
  appear in a row whose layout is '1', as the only element of the only column.
  Exported because all three places that enforce it need the same list — the
  schema refinement, the editor's element palette (greyed out with a reason) and
  whatever endpoint parses a document.
*/
export const FULL_WIDTH_ELEMENT_TYPES = [
  'contactForm',
  'reservation',
  'menuFromPos',
  'video',
  'map'
] as const

export type FullWidthElementType = (typeof FULL_WIDTH_ELEMENT_TYPES)[number]

const base = { id: idSchema }

export const headingElementSchema = z.object({
  ...base,
  type: z.literal('heading'),
  /*
    Two levels, not six. The page's `h1` is the site header's business, so an
    owner cannot accidentally produce a document with three competing top-level
    headings, which is the most common way a hand-built page loses its outline.
  */
  level: z.union([z.literal(2), z.literal(3)]),
  text: localizedTextSchema,
  align: alignSchema
})

export const textElementSchema = z.object({
  ...base,
  type: z.literal('text'),
  content: localized(richTextDocSchema),
  align: alignSchema
})

export const imageElementSchema = z.object({
  ...base,
  type: z.literal('image'),
  assetId: assetRefSchema,
  /*
    Translatable and required at publication, not at save. A draft with a missing
    alt must still be storable, or the editor loses work every time someone
    uploads a photo and walks away; the publish-time schema is what refuses it.
  */
  alt: localizedTextSchema,
  ratio: z.enum(['1:1', '4:3', '16:9', '3:4', 'original']),
  fit: z.enum(['cover', 'contain']),
  /** Which part of the photo survives a crop. 0..1 of width and height. */
  focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
  rounded: z.boolean(),
  caption: localizedTextSchema.optional(),
  href: z.string().url().max(2048).optional()
})

/**
 * Where a button goes.
 *
 * A union rather than the brief's `href | action`, so the three cases cannot be
 * confused: an internal page keeps working when its slug changes (the id is
 * stored, the URL is resolved at render), an external link is validated as a
 * URL, and an action is a fixed name the renderer knows how to wire.
 */
export const buttonTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('url'),
    href: z.string().url().max(2048),
    /** Another site's page, such as Google's review form, opens beside this one. */
    newTab: z.boolean().optional()
  }),
  z.object({ kind: z.literal('page'), pageId: idSchema }),
  z.object({
    kind: z.literal('action'),
    action: z.enum(['reservation', 'phone', 'email'])
  })
])

export const buttonElementSchema = z.object({
  ...base,
  type: z.literal('button'),
  label: localizedTextSchema,
  target: buttonTargetSchema,
  variant: z.enum(['primary', 'secondary', 'outline']),
  align: alignSchema
})

export const galleryElementSchema = z.object({
  ...base,
  type: z.literal('gallery'),
  assetIds: z.array(assetRefSchema).min(1).max(MAX_GALLERY_IMAGES),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  lightbox: z.boolean()
})

/*
  Only two providers, and the URL has to match the one that was chosen. A video
  block that accepted any URL would be an open embed, which is an iframe pointing
  wherever the payload says.
*/
const YOUTUBE_URL = /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/i
const VIMEO_URL = /^https:\/\/(www\.)?vimeo\.com\//i

export const videoElementSchema = z
  .object({
    ...base,
    type: z.literal('video'),
    provider: z.enum(['youtube', 'vimeo']),
    url: z.string().url().max(2048)
  })
  .refine(
    value =>
      value.provider === 'youtube'
        ? YOUTUBE_URL.test(value.url)
        : VIMEO_URL.test(value.url),
    { message: 'video url does not match the chosen provider', path: ['url'] }
  )

export const MAP_HEIGHTS = ['S', 'M', 'L'] as const

export type MapHeight = (typeof MAP_HEIGHTS)[number]

/*
  The venue's own map, on Google's free Maps Embed, at the URL the backend
  built for the venue's Google place. Copied into the document like the
  address is, so a published page draws it without asking anyone.

  Empty until the venue is connected: the editor then shows where the map
  would go and the published page leaves it out. The defaults are what a map
  stored before this shape (an address and a zoom) parses into.
*/
export const mapElementSchema = z.object({
  ...base,
  type: z.literal('map'),
  embedUrl: z
    .string()
    .max(2048)
    .refine(value => value === '' || isGoogleMapsEmbedUrl(value), {
      message: 'not a Google Maps embed'
    })
    .default(''),
  height: z.enum(MAP_HEIGHTS).default('M')
})

export const openingHoursEntrySchema = z.object({
  /** 0 = Monday, matching how the roster in this repo already counts days. */
  weekday: z.number().int().min(0).max(6),
  closed: z.boolean(),
  opensAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  closesAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
})

export const openingHoursElementSchema = z.object({
  ...base,
  type: z.literal('openingHours'),
  /*
    Manual only. `menu_core_be` has no opening hours for a venue at all
    (`shifts` is the staff roster), so a 'pos' source would render nothing.
    It comes back when that entity exists, which the mobile app wants too.
  */
  source: z.literal('manual'),
  entries: z.array(openingHoursEntrySchema).max(7)
})

export const menuFromPosElementSchema = z.object({
  ...base,
  type: z.literal('menuFromPos'),
  /*
    Ids, resolved against `/public/restaurants/{id}/categories` at render. A
    category deleted from the menu must not break a published page, so the
    renderer skips ids it cannot resolve, the way loyalty rewards skip dishes
    that no longer exist.
  */
  categoryIds: z.array(idSchema).max(50),
  layout: z.enum(['list', 'grid']),
  showPrices: z.boolean(),
  showPhotos: z.boolean()
})

export const contactFormElementSchema = z.object({
  ...base,
  type: z.literal('contactForm'),
  formId: idSchema
})

/**
 * One room, copied out of the floor planner.
 *
 * A copy and not a reference, and that is the whole design. The published page
 * is prerendered to static HTML with no session, so it cannot call the planner's
 * API; and a table dragged across the planner on a Tuesday must not silently
 * redraw a page published in March. The owner re-picks the room to take a new
 * layout, which is the same bargain the rest of the template makes with the
 * venue's facts.
 */
export const roomPlanSchema = z.object({
  id: idSchema,
  name: z.string().max(80),
  tables: z
    .array(
      z.object({
        id: idSchema,
        label: z.string().max(16),
        shape: z.enum(['round', 'square', 'rectangle']),
        seatCount: z.number().int().min(0).max(99),
        x: z.number(),
        y: z.number(),
        width: z.number().positive(),
        height: z.number().positive(),
        rotation: z.number()
      })
    )
    .max(MAX_TABLES_PER_ROOM)
})

export type RoomPlan = z.infer<typeof roomPlanSchema>

export const reservationElementSchema = z.object({
  ...base,
  type: z.literal('reservation'),
  /*
    No `settingsId`. It pointed at reservation settings that were never built,
    and nothing read it once the block started carrying its own room, telephone
    and label. It was worse than unused: `createElement` seeded it with an empty
    string, which `idSchema` refuses, so every reservation block dropped in the
    builder produced a document that would not parse. Zod strips unknown keys,
    so documents still carrying one open unchanged.
  */
  variant: z.enum(['inline', 'button']),
  /*
    Every room a guest may book a table in, drawn one behind a tab each. A list
    and not one room: a venue with a dining room, a garden and a mezzanine has
    three rooms a guest might want to sit in, and choosing one to be the room
    would be choosing for them.

    The plan is the picker (PLAN.md §5, mode B): every table on it is a choice
    in the booking form, and the form goes out with the table's id. Optional
    and additive, so a document written before the block drew anything still
    parses; without rooms there is no table to pick and no form.
  */
  rooms: z.array(roomPlanSchema).max(MAX_ROOMS_PER_BLOCK).optional(),
  /*
    A number for the guest who would rather ring, printed under the form. Not a
    button any more: the form is the booking, and a button that dialled the
    venue next to a form that books it was two answers to one question.
  */
  phone: z.string().max(40).optional(),
  /** What the form's button says. The renderer has a default per language. */
  label: localizedTextSchema.optional(),
  /*
    The line a guest agrees to before the form goes out: who holds their name
    and number, and what for. The brief (§7) makes the information clause a
    hard requirement on every form. Editable, because the controller is the
    venue and its legal name is its own to state; the template writes a first
    version naming it.
  */
  consent: localizedTextSchema.optional(),
  /** The most people one booking may be for; the form refuses more. */
  maxGuests: z.number().int().min(1).max(99).optional(),
  /*
    The hour the booking form opens on, `HH:mm`, which the template takes from
    the venue's own opening times. A default and not a limit: a guest changes
    it. It exists because both fields stand outside the sheet that submits the
    form, and a required field a guest cannot see is a submit that fails with
    nothing on screen to explain it.
  */
  defaultTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  /*
    The hours the form offers, `HH:mm` to `HH:mm`, which the template takes
    from the venue's opening times: the earliest it opens and the latest it
    closes in a week. The page lists every quarter hour between them and a
    guest picks one, rather than typing into the browser's own clock.
  */
  bookingHours: z
    .object({
      from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    })
    .optional()
})

export const dividerElementSchema = z.object({
  ...base,
  type: z.literal('divider')
})

export const spacerElementSchema = z.object({
  ...base,
  type: z.literal('spacer'),
  size: spacingTokenSchema
})

/*
  `z.union`, not `z.discriminatedUnion`: the video element carries a `.refine`,
  which makes it a ZodEffects rather than a ZodObject, and discriminated unions
  only accept plain objects. The cost is a worse error message on an unknown
  type, which `elementTypeSchema` below buys back for the callers that care.
*/
export const elementSchema = z.union([
  headingElementSchema,
  textElementSchema,
  imageElementSchema,
  buttonElementSchema,
  galleryElementSchema,
  videoElementSchema,
  mapElementSchema,
  openingHoursElementSchema,
  menuFromPosElementSchema,
  contactFormElementSchema,
  reservationElementSchema,
  dividerElementSchema,
  spacerElementSchema
])

export const elementTypeSchema = z.enum([
  'heading',
  'text',
  'image',
  'button',
  'gallery',
  'video',
  'map',
  'openingHours',
  'menuFromPos',
  'contactForm',
  'reservation',
  'divider',
  'spacer'
])

export type ElementType = z.infer<typeof elementTypeSchema>
export type PageElement = z.infer<typeof elementSchema>

export function isFullWidthElement(element: PageElement): boolean {
  return (FULL_WIDTH_ELEMENT_TYPES as readonly string[]).includes(element.type)
}
